import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Loader2, Sparkles, CheckCircle2, AlertCircle, Wand2, X } from 'lucide-react';
import { profilingApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * AutoProfileDialog — Preview & confirm modal for company auto-profiling.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v) => void
 *  - companyIds: string[] | null  (null = ALL companies; [] would mean nothing)
 *  - onApplied: () => void        (callback after successful apply, for parent reload)
 */
const labelFor = (key) => ({
  over15Dipendenti: 'Oltre 15 dipendenti',
  macrosettore: 'Macrosettore',
  controlloVVF: 'Controllo VVF',
  profili: 'Profili marketing',
}[key] || key);

const renderValue = (k, v) => {
  if (v === null || v === undefined || v === '') return <span className="text-slate-400 italic">vuoto</span>;
  if (typeof v === 'boolean') return v ? 'Sì' : 'No';
  if (Array.isArray(v)) return v.length ? v.map((p) => p.replace(/_/g, ' ')).join(', ') : <span className="text-slate-400 italic">—</span>;
  if (typeof v === 'string') return v.replace(/_/g, ' ');
  return String(v);
};

const AutoProfileDialog = ({ open, onOpenChange, companyIds, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [items, setItems] = useState([]);
  const [aiUsed, setAiUsed] = useState(0);
  const [aiFailed, setAiFailed] = useState(0);
  const [useAI, setUseAI] = useState(true);
  // selections[companyId] = { [fieldName]: true|false }
  const [selections, setSelections] = useState({});

  const fetchPreview = async (withAI) => {
    setLoading(true);
    try {
      const body = { useAI: withAI };
      if (Array.isArray(companyIds)) body.companyIds = companyIds;
      const res = await profilingApi.preview(body);
      setItems(res.data.items || []);
      setAiUsed(res.data.ai_used || 0);
      setAiFailed(res.data.ai_failed || 0);
      // Pre-select all proposed fields
      const sel = {};
      (res.data.items || []).forEach((it) => {
        sel[it.companyId] = {};
        Object.keys(it.proposed || {}).forEach((f) => { sel[it.companyId][f] = true; });
      });
      setSelections(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'anteprima');
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setItems([]);
      setSelections({});
      fetchPreview(useAI);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (cid, field) => {
    setSelections((s) => ({ ...s, [cid]: { ...(s[cid] || {}), [field]: !((s[cid] || {})[field]) } }));
  };

  const toggleAllForCompany = (cid, value) => {
    const it = items.find((x) => x.companyId === cid);
    if (!it) return;
    const next = {};
    Object.keys(it.proposed || {}).forEach((f) => { next[f] = value; });
    setSelections((s) => ({ ...s, [cid]: next }));
  };

  const counters = useMemo(() => {
    let companies = 0;
    let fields = 0;
    items.forEach((it) => {
      const sel = selections[it.companyId] || {};
      const chosen = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
      if (chosen.length) { companies += 1; fields += chosen.length; }
    });
    return { companies, fields };
  }, [items, selections]);

  const applyChanges = async () => {
    const applications = items
      .map((it) => {
        const sel = selections[it.companyId] || {};
        const fields = {};
        Object.keys(it.proposed || {}).forEach((f) => {
          if (sel[f]) fields[f] = it.proposed[f];
        });
        return { companyId: it.companyId, fields };
      })
      .filter((a) => Object.keys(a.fields).length > 0);
    if (!applications.length) {
      toast.warning('Nessun campo selezionato');
      return;
    }
    setApplying(true);
    try {
      const res = await profilingApi.apply(applications);
      toast.success(`Profilazione applicata a ${res.data.updated} aziend${res.data.updated === 1 ? 'a' : 'e'}`);
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il salvataggio');
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="auto-profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 size={18} className="text-[#023F80]" />
            Profilazione automatica aziende
          </DialogTitle>
          <DialogDescription>
            Analizziamo nome, partita IVA, lavoratori, formazioni e documenti per proporre Macrosettore, &gt;15 dipendenti, Controllo VVF e tag di profilazione. <strong>Vengono compilati solo i campi vuoti.</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 px-1 py-2 border-y border-slate-100">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={useAI} onCheckedChange={(v) => { setUseAI(!!v); fetchPreview(!!v); }} data-testid="auto-profile-use-ai" />
            <Sparkles size={13} className="text-amber-500" /> Usa AI (Claude Sonnet 4.5) per i nomi non classificati dalle regole
          </label>
          <div className="flex items-center gap-3 text-xs text-slate-600">
            {aiUsed > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                <Sparkles size={11} /> AI: {aiUsed - aiFailed}/{aiUsed} classificate
              </span>
            )}
            <span className="font-semibold text-[#023F80]">
              {items.length} aziend{items.length === 1 ? 'a' : 'e'} con proposte
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto -mx-2 px-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="animate-spin text-[#023F80]" size={28} />
              <span className="text-sm text-slate-600">Analisi in corso… {useAI ? '(coinvolta AI)' : ''}</span>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-500">
              <CheckCircle2 size={32} className="text-emerald-500" />
              <span className="text-sm font-semibold">Nessuna proposta: i campi sono già tutti compilati.</span>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              {items.map((it) => {
                const sel = selections[it.companyId] || {};
                const proposedKeys = Object.keys(it.proposed || {});
                const allChecked = proposedKeys.every((f) => sel[f]);
                return (
                  <div key={it.companyId} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`auto-profile-row-${it.companyId}`}>
                    <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
                      <div className="font-semibold text-slate-800 text-sm">{it.name}</div>
                      <button
                        type="button"
                        onClick={() => toggleAllForCompany(it.companyId, !allChecked)}
                        className="text-[11px] font-bold uppercase tracking-wider text-[#023F80] hover:underline"
                        data-testid={`auto-profile-toggle-all-${it.companyId}`}
                      >
                        {allChecked ? 'Deseleziona tutto' : 'Seleziona tutto'}
                      </button>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 w-10"></th>
                          <th className="text-left px-3 py-2">Campo</th>
                          <th className="text-left px-3 py-2">Attuale</th>
                          <th className="text-left px-3 py-2">Proposto</th>
                          <th className="text-left px-3 py-2">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proposedKeys.map((f) => (
                          <tr key={f} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <Checkbox
                                checked={!!sel[f]}
                                onCheckedChange={() => toggle(it.companyId, f)}
                                data-testid={`auto-profile-checkbox-${it.companyId}-${f}`}
                              />
                            </td>
                            <td className="px-3 py-2 font-semibold text-slate-700">{labelFor(f)}</td>
                            <td className="px-3 py-2 text-slate-500">{renderValue(f, it.current?.[f])}</td>
                            <td className="px-3 py-2 text-emerald-700 font-semibold">{renderValue(f, it.proposed[f])}</td>
                            <td className="px-3 py-2 text-xs text-slate-500 italic">{it.reasons?.[f] || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-3 sm:justify-between">
          <div className="text-xs text-slate-600">
            {counters.companies > 0 ? (
              <span><strong>{counters.companies}</strong> aziend{counters.companies === 1 ? 'a' : 'e'} • <strong>{counters.fields}</strong> camp{counters.fields === 1 ? 'o' : 'i'} selezionat{counters.fields === 1 ? 'o' : 'i'}</span>
            ) : items.length > 0 ? (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle size={12} /> Seleziona almeno un campo</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700 flex items-center gap-1"
              data-testid="auto-profile-cancel"
            >
              <X size={14} /> Annulla
            </button>
            <button
              onClick={applyChanges}
              disabled={applying || loading || counters.companies === 0}
              className="ro-hide px-4 py-2 rounded-lg bg-[#023F80] hover:bg-[#1e293b] disabled:opacity-50 text-sm font-bold text-white flex items-center gap-1"
              data-testid="auto-profile-apply"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} className="text-[#FFCB05]" />}
              Applica selezionati
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoProfileDialog;
